/**
 * Named, reloadable placement layouts for the Coral Placer. The operator can
 * snapshot the current placements on an aquascape under a name and reload it
 * later. Pure ops + a defensive normalizer here; the Pi-backed hook lives in
 * `persistence.ts`.
 */
import type { Placement, PlacementPhoto } from './types';

export interface SavedLayout {
  readonly id: string;
  readonly name: string;
  /** Aquascape this layout was captured on (so Load can restore it there). */
  readonly aquascapeId: string;
  readonly placements: readonly Placement[];
  readonly createdAt: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function photo(v: unknown): PlacementPhoto {
  return v === 'colony' ? 'colony' : 'frag';
}

function normalizePlacement(raw: unknown): Placement | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o['id']);
  const coralId = str(o['coralId']);
  if (id === '' || coralId === '') return null;
  return {
    id,
    coralId,
    xPct: clamp(num(o['xPct'], 0.5), 0, 1),
    yPct: clamp(num(o['yPct'], 0.5), 0, 1),
    sizePct: clamp(num(o['sizePct'], 0.05), 0.02, 0.16),
    photo: photo(o['photo']),
  };
}

function normalizeLayout(raw: unknown): SavedLayout | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o['id']);
  const name = str(o['name']);
  const aquascapeId = str(o['aquascapeId']);
  if (id === '' || name === '' || aquascapeId === '') return null;
  const listRaw = Array.isArray(o['placements']) ? o['placements'] : [];
  const placements: Placement[] = [];
  for (const p of listRaw) {
    const n = normalizePlacement(p);
    if (n) placements.push(n);
  }
  return {
    id,
    name,
    aquascapeId,
    placements,
    createdAt: num(o['createdAt'], 0),
  };
}

/** Coerce an unknown persisted blob into a valid array of saved layouts. */
export function normalizeSavedLayouts(raw: unknown): SavedLayout[] {
  if (!Array.isArray(raw)) return [];
  const out: SavedLayout[] = [];
  for (const entry of raw) {
    const n = normalizeLayout(entry);
    if (n) out.push(n);
  }
  return out;
}

export function addSavedLayout(
  layouts: readonly SavedLayout[],
  layout: SavedLayout,
): SavedLayout[] {
  return [layout, ...layouts];
}

export function removeSavedLayout(
  layouts: readonly SavedLayout[],
  id: string,
): SavedLayout[] {
  return layouts.filter((l) => l.id !== id);
}

export function findSavedLayout(
  layouts: readonly SavedLayout[],
  id: string,
): SavedLayout | undefined {
  return layouts.find((l) => l.id === id);
}
